import { useEffect, useRef, useState, useMemo } from 'react';
import ManImg from './images/man.png';
import WomanImg from './images/woman.png';
import { layoutTree, drawTree } from './utils';
import type { Person, LayoutMap, FamilyTreeCanvasProps } from './types';

export const useFamilyTreeCanvas = ({ people, rootId }: FamilyTreeCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [positions, setPositions] = useState<LayoutMap>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [showMenu, setShowMenu] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [lastTouch, setLastTouch] = useState<{ x: number; y: number; distance?: number } | null>(null);

  const manImage = useMemo(() => {
    const img = new Image();
    img.src = ManImg;
    return img;
  }, []);

  const womanImage = useMemo(() => {
    const img = new Image();
    img.src = WomanImg;
    return img;
  }, []);

  const peopleMap = useMemo(() => {
    const map: Record<string, Person> = {};
    people.forEach(p => { map[p.id] = p; });
    return map;
  }, [people]);

  useEffect(() => {
    const layout: LayoutMap = {};
    layoutTree(peopleMap, rootId, 100, 50, 40, layout);
    setPositions(layout);
  }, [peopleMap, rootId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || Object.keys(positions).length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth * ratio;
    const height = canvas.clientHeight * ratio;
    canvas.width = width;
    canvas.height = height;

    drawTree(ctx, positions, offset, scale, peopleMap, selectedId || '', manImage, womanImage);
  }, [positions, offset, scale, selectedId]);

  // Mouse handlers (for desktop)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      setIsDragging(true);
      setLastPos({ x: e.clientX, y: e.clientY });
      canvas.style.cursor = 'grabbing';
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - lastPos.x;
      const dy = e.clientY - lastPos.y;
      setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setLastPos({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      canvas.style.cursor = 'grab';
    };

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      setShowMenu(false);
      const zoom = e.deltaY < 0 ? 0.1 : -0.1;
      const newScale = Math.max(0.2, Math.min(4, scale + zoom));

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const worldX = (mouseX - offset.x) / scale;
      const worldY = (mouseY - offset.y) / scale;
      const newOffsetX = mouseX - worldX * newScale;
      const newOffsetY = mouseY - worldY * newScale;

      setScale(newScale);
      setOffset({ x: newOffsetX, y: newOffsetY });
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [isDragging, scale, offset, lastPos]);

  // Touch handlers (for mobile)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        setIsDragging(true);
        const touch = e.touches[0];
        setLastPos({ x: touch.clientX, y: touch.clientY });
      } else if (e.touches.length === 2) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        setLastTouch({ x: (touch1.clientX + touch2.clientX) / 2, y: (touch1.clientY + touch2.clientY) / 2, distance });
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1 && isDragging) {
        const touch = e.touches[0];
        const dx = touch.clientX - lastPos.x;
        const dy = touch.clientY - lastPos.y;
        setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
        setLastPos({ x: touch.clientX, y: touch.clientY });
      } else if (e.touches.length === 2 && lastTouch) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const scaleChange = distance / lastTouch.distance!;
        setScale(prevScale => Math.max(0.2, Math.min(4, prevScale * scaleChange)));
        setLastTouch({ x: (touch1.clientX + touch2.clientX) / 2, y: (touch1.clientY + touch2.clientY) / 2, distance });
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      setLastTouch(null);
    };

    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove', handleTouchMove);
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, lastPos, scale, lastTouch]);

  const handleCanvasClick = (e: React.MouseEvent) => {
    if (e.button !== 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Translate to world coordinates
    const worldX = (x - offset.x) / scale;
    const worldY = (y - offset.y) / scale;

    const radius = 40;
    let found = false;

    // Check if the click is inside the radius of any person
    for (const person of people) {
      const pos = positions[person.id];
      if (!pos) continue;

      const personX = pos.x + 60;
      const personY = pos.y + 20;

      if (
        Math.abs(worldX - personX) < radius &&
        Math.abs(worldY - personY) < radius
      ) {
        setSelectedId(person.id);
        setMenuPosition({ x, y });
        setShowMenu(true);
        found = true;
        break;
      }
    }

    // If no person was clicked, hide the menu
    if (!found) {
      setSelectedId(null);
      setShowMenu(false);
    }
  };

  const handleSelect = (id: string) => {
    const pos = positions[id];
    if (!pos) return;
    setSelectedId(id);
    const centerX = pos.x + 60;
    const centerY = pos.y + 20;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const scaleFactor = scale;
    const offsetX = canvas.width / 2 - centerX * scaleFactor;
    const offsetY = canvas.height / 2 - centerY * scaleFactor;
    setOffset({ x: offsetX, y: offsetY });
  };

  return { canvasRef, handleCanvasClick, selectedId, menuPosition, showMenu, handleSelect };
};
